import { prefersReducedMotion } from '@theme/utilities';

/**
 * Lightweight controller for the cart free-shipping badge.
 *
 * The cart drawer is morph-updated after every cart change, so this element
 * only preserves the outgoing locked label long enough to play a whole-pill
 * swap into the unlocked state.
 */
class FreeShippingBadge extends HTMLElement {
  static observedAttributes = ['data-state', 'data-away-label', 'data-away-value', 'data-away-suffix'];

  /** @type {number | null} */
  #animationFrame = null;

  /** @type {number | null} */
  #cleanupTimer = null;

  /** @type {number | null} */
  #relockTimer = null;

  #hasConnected = false;

  connectedCallback() {
    this.#hasConnected = true;
    this.#syncState();
  }

  disconnectedCallback() {
    this.#cancelPendingWork();
    this.#hasConnected = false;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.#hasConnected) return;
    if (oldValue === newValue) return;

    if (name === 'data-state') {
      if (oldValue === 'locked' && newValue === 'unlocked') {
        this.#playUnlock(this.#getCurrentAwayLabel());
        return;
      }

      if (oldValue === 'unlocked' && newValue === 'locked') {
        this.#playRelock();
      }

      this.#syncState();
      return;
    }

    if (
      (name === 'data-away-label' || name === 'data-away-value' || name === 'data-away-suffix')
      && this.dataset.state !== 'unlocked'
    ) {
      this.#syncState();
    }
  }

  #playUnlock(outgoingLabel) {
    this.#cancelPendingWork();

    if (prefersReducedMotion()) {
      this.#syncState();
      return;
    }

    this.#animationFrame = window.requestAnimationFrame(() => {
      const refs = this.#getRefs();

      if (!refs) return;

      refs.awayText.textContent = outgoingLabel;
      refs.awayBadge.setAttribute('aria-hidden', 'false');
      refs.unlockedBadge.setAttribute('aria-hidden', 'false');

      if (refs.checkPath) {
        refs.checkPath.style.strokeDashoffset = '13';
      }

      this.classList.remove('fs-badge--unlocking');
      void this.offsetWidth;
      this.classList.add('fs-badge--unlocking');

      this.#cleanupTimer = window.setTimeout(() => {
        this.#syncState();
      }, 420);
    });
  }

  #syncState() {
    const refs = this.#getRefs();

    if (!refs) return;

    const unlocked = this.dataset.state === 'unlocked';
    const awayValue = this.dataset.awayValue?.trim();
    const awaySuffix = this.dataset.awaySuffix?.trim();

    this.#cancelPendingWork();
    this.classList.toggle('fs-badge--is-unlocked', unlocked);

    if (!unlocked) {
      if (awayValue) {
        refs.awayValue.textContent = awayValue;
      }

      if (awaySuffix) {
        refs.awaySuffix.textContent = ` ${awaySuffix}`;
      }
    }

    refs.awayBadge.setAttribute('aria-hidden', unlocked ? 'true' : 'false');
    refs.unlockedBadge.setAttribute('aria-hidden', unlocked ? 'false' : 'true');

    if (refs.checkPath) {
      refs.checkPath.style.strokeDashoffset = unlocked ? '0' : '13';
    }
  }

  #getCurrentAwayLabel() {
    const refs = this.#getRefs();

    if (!refs) {
      return this.dataset.awayLabel?.trim() || '';
    }

    return refs.awayText.textContent?.trim() || this.dataset.awayLabel?.trim() || '';
  }

  #playRelock() {
    if (prefersReducedMotion()) return;

    const wrapper = this.closest('.cart-drawer__free-shipping');

    if (!wrapper) return;

    if (this.#relockTimer !== null) {
      window.clearTimeout(this.#relockTimer);
    }

    wrapper.classList.add('cart-drawer__free-shipping--relocking');
    this.#relockTimer = window.setTimeout(() => {
      wrapper.classList.remove('cart-drawer__free-shipping--relocking');
      this.#relockTimer = null;
    }, 900);
  }

  #cancelPendingWork() {
    if (this.#animationFrame !== null) {
      window.cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }

    if (this.#cleanupTimer !== null) {
      window.clearTimeout(this.#cleanupTimer);
      this.#cleanupTimer = null;
    }

    if (this.#relockTimer !== null) {
      window.clearTimeout(this.#relockTimer);
      const wrapper = this.closest('.cart-drawer__free-shipping');
      wrapper?.classList.remove('cart-drawer__free-shipping--relocking');
      this.#relockTimer = null;
    }

    this.classList.remove('fs-badge--unlocking');
  }

  #getRefs() {
    const awayBadge = /** @type {HTMLElement | null} */ (this.querySelector('[ref="awayBadge"]'));
    const awayText = /** @type {HTMLElement | null} */ (this.querySelector('[ref="awayText"]'));
    const awayValue = /** @type {HTMLElement | null} */ (this.querySelector('[ref="awayValue"]'));
    const awaySuffix = /** @type {HTMLElement | null} */ (this.querySelector('[ref="awaySuffix"]'));
    const unlockedBadge = /** @type {HTMLElement | null} */ (this.querySelector('[ref="unlockedBadge"]'));
    const checkPath = /** @type {SVGPathElement | null} */ (this.querySelector('.fs-badge__check path'));

    if (!awayBadge || !awayText || !awayValue || !awaySuffix || !unlockedBadge) {
      return null;
    }

    return { awayBadge, awayText, awayValue, awaySuffix, unlockedBadge, checkPath };
  }
}

if (!customElements.get('free-shipping-badge')) {
  customElements.define('free-shipping-badge', FreeShippingBadge);
}
